/**
 * TenantSwitcher — dropdown in the sidebar that scopes the entire app to
 * one tenant slug (or "all" for admin/operator).
 *
 * Selecting a tenant updates the app-wide TenantContext (src/lib/tenant.ts,
 * persisted to localStorage) AND navigates into that tenant's home, so the
 * sidebar's tenant-scoped sections and every screen follow. "Alle tenants"
 * returns to the global dashboard.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { ChevronDown, Check } from './icons';
import { StatusDot, type DotStatus } from './StatusDot';
import { useTenant } from '@/lib/tenant';
import { cn } from './cn';

export interface TenantOption {
  slug: string;
  name: string;
  status?: DotStatus;
}

export interface TenantSwitcherProps {
  tenants: readonly TenantOption[];
  /** True if the current user may see the "Alle tenants" pseudo-option. */
  allowAll?: boolean;
  className?: string;
}

const ALL = 'all' as const;

export function TenantSwitcher({
  tenants,
  allowAll = true,
  className,
}: TenantSwitcherProps): JSX.Element {
  const navigate = useNavigate();
  // The selected tenant is the app-wide TenantContext slug (kept in sync by the
  // /tenants/$slug routes' setSlug effect + localStorage). Selecting here both
  // updates that context AND navigates into the tenant, so the sidebar + every
  // tenant-scoped screen follow. Previously this only wrote a ?tenant= search
  // param that nothing consumed, so selecting a tenant did nothing.
  const { slug: currentSlug, setSlug } = useTenant();

  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent): void => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onEsc = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open]);

  const handleSelect = useCallback(
    (slug: string) => {
      setOpen(false);
      setSlug(slug);
      if (slug === ALL) {
        void navigate({ to: '/' });
      } else {
        void navigate({ to: '/tenants/$slug', params: { slug } });
      }
    },
    [navigate, setSlug],
  );

  const active =
    currentSlug === ALL
      ? { slug: ALL, name: 'Alle tenants', status: 'good' as DotStatus }
      : (tenants.find((t) => t.slug === currentSlug) ?? {
          slug: currentSlug,
          name: currentSlug,
          status: 'onboarding' as DotStatus,
        });

  return (
    <div ref={rootRef} className={cn('relative', className)}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label="Tenant kiezen"
        className="flex w-full items-center justify-between gap-2 rounded border border-border-subtle bg-surface-subtle px-2.5 py-1.5 text-xs text-fg-secondary transition hover:border-border"
      >
        <span className="flex min-w-0 items-center gap-2 truncate">
          <StatusDot status={active.status ?? 'good'} />
          <span className="truncate">{active.name}</span>
        </span>
        <ChevronDown size={12} className="text-fg-muted" aria-hidden="true" />
      </button>

      {open ? (
        <ul
          role="listbox"
          aria-label="Tenants"
          className="absolute left-0 right-0 z-dropdown mt-1 max-h-80 overflow-y-auto rounded-md border border-border-subtle bg-surface py-1 shadow-md"
        >
          {allowAll ? (
            <TenantRow
              option={{ slug: ALL, name: 'Alle tenants', status: 'good' }}
              selected={currentSlug === ALL}
              onSelect={handleSelect}
            />
          ) : null}
          {tenants.map((t) => (
            <TenantRow
              key={t.slug}
              option={t}
              selected={currentSlug === t.slug}
              onSelect={handleSelect}
            />
          ))}
          {tenants.length === 0 ? (
            <li className="px-3 py-2 text-xs text-fg-muted">Geen tenants beschikbaar.</li>
          ) : null}
        </ul>
      ) : null}
    </div>
  );
}

interface TenantRowProps {
  option: TenantOption;
  selected: boolean;
  onSelect: (slug: string) => void;
}

function TenantRow({ option, selected, onSelect }: TenantRowProps): JSX.Element {
  return (
    <li>
      <button
        type="button"
        role="option"
        aria-selected={selected}
        onClick={() => onSelect(option.slug)}
        className={cn(
          'flex w-full items-center justify-between gap-2 px-3 py-1.5 text-left text-xs hover:bg-surface-subtle',
          selected ? 'text-fg-primary' : 'text-fg-secondary',
        )}
      >
        <span className="flex min-w-0 items-center gap-2 truncate">
          <StatusDot status={option.status ?? 'good'} />
          <span className="truncate">{option.name}</span>
        </span>
        {selected ? <Check size={12} className="text-brand-600" aria-hidden="true" /> : null}
      </button>
    </li>
  );
}
